import { useRef, useState } from 'react';
import axios from "axios";
import { appId, ticketBaseURL } from '../../config/api';
import { Helmet } from 'react-helmet-async';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css'
import { validatePhoneNumber } from '../../utils/validatePhoneNumber';

const ContactUs = () => {
  const [email, setEmail] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [phone, setPhone] = useState<any>("");
  const [question, setQuestion] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);


  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);

    const isValid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/.test(value);
    e.target.setCustomValidity(isValid ? "" : "Please enter a valid email");
    e.target.reportValidity()
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isProcessing) return;

    validatePhoneNumber(phone, inputRef.current);

    if (!inputRef.current?.checkValidity()) {
      inputRef?.current?.reportValidity();
      return;
    }

    setIsProcessing(true);

    await axios
      .post(
        `${ticketBaseURL}customerEnquiry`,
        JSON.stringify({
          email,
          name,
          phone,
          question,
          applicationId: appId,
          created: new Date(),
        }),
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        }
      )
      .then(() => {
        resetForm();
        setSubmitted(true);
      })
      .catch((error) => {
        setIsProcessing(false);
        console.error("Error:", error);
      });
  };

  const resetForm = () => {
    setEmail("");
    setName("");
    setPhone("");
    setQuestion("");
    setIsProcessing(false);
  };

  return (
    <>
      <Helmet>
        <title>My Church Directory Contact Us | Online Church Directory Software</title>

        <meta
          name="description"
          content="Explore powerful church directory features including member management, mobile apps, email and text messaging, printable directories, and secure online access. Manage your church directory with ease."
        />
      </Helmet>

      <section className="bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex rounded-full bg-sky-100 text-secondary px-4 py-1 text-sm font-medium">
              Contact Us
            </span>

            <h1 className="different_heading mt-5 text-4xl lg:text-5xl font-bold text-slate-900">
              We'd Love to Hear From You
            </h1>

            <p className="mt-6 text-lg text-slate-600 leading-8">
              Have questions about My Church Directory? Our support team is here to
              help with setup, troubleshooting, and anything else you need.
            </p>
          </div>

          <div>
            <img
              src="../images/contact-us-hero.jpg"
              alt="Contact My Church Directory"
              className="rounded-3xl shadow-xl w-full"
            />
          </div>
        </div>
      </section>
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">

          <div className="text-center mb-14">
            <h2 className="different_heading text-3xl font-bold">
              Our Offices
            </h2>

            <p className="mt-3 text-slate-600">
              Visit us or reach out to our support team.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">

            {/* Spring */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-6">
                <h3 className="different_heading text-2xl font-bold">Spring, Texas</h3>

                <p className="mt-3 text-slate-600">
                  24624 Interstate 45 N Suite 200
                  <br />
                  Spring, TX 77386
                </p>
              </div>

              <iframe
                title="Google Map" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3451.0952715809685!2d-95.44079649999999!3d30.1200872!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8647343421fd0135%3A0xa8a51ce8ee769957!2s24624%20Interstate%2045%20N%20Suite%20200%2C%20Spring%2C%20TX%2077386%2C%20USA!5e0!3m2!1sen!2sin!4v1699002913944!5m2!1sen!2sin" width="100%" height="350" style={{ border: 0, padding: "10px" }} loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            {/* Sugar Land */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-6">
                <h3 className="different_heading text-2xl font-bold">
                  Sugar Land, Texas
                </h3>

                <p className="mt-3 text-slate-600">
                  54 Sugar Creek Center Blvd STE 300
                  <br />
                  Sugar Land, TX 77478
                </p>
              </div>

              <iframe
                title="Google Map" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3468.397953022852!2d-95.60810362544842!3d29.62118303850247!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8640e6ff0ed3930f%3A0x76bf2c10013f2a4b!2s54%20Sugar%20Creek%20Center%20Blvd%20STE%20300%2C%20Sugar%20Land%2C%20TX%2077478%2C%20USA!5e0!3m2!1sen!2sin!4v1699003331966!5m2!1sen!2sin" width="100%" height="350" style={{ border: '0', padding: "10px" }} loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

          </div>

        </div>
      </section>
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-6">

          <div className="text-center mb-12">
            <h2 className="different_heading text-3xl font-bold">
              Send Us a Message
            </h2>

            <p className="mt-3 text-slate-600">
              We'll get back to you within 24–48 business hours.
            </p>
          </div>

          {submitted && (
            <div className="mb-8 rounded-xl bg-green-50 border border-green-200 p-4 text-center text-green-700">
              Thank you! We'll be in touch soon.
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 space-y-6"
          >

            <div className="grid md:grid-cols-2 gap-6">

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Name
                </label>

                <input
                  type="text"
                  placeholder="Enter your full name"
                  className="h-12 w-full rounded-lg border border-gray-300 px-4 text-sm outline-none transition-colors
    focus:border-secondary focus:ring-2 focus:ring-secondary/20"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email Address
                </label>

                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={handleChange}
                  placeholder="Enter your email address"
                  required
                  className="h-12 w-full rounded-lg border border-gray-300 px-4 text-sm outline-none transition-colors
    focus:border-secondary focus:ring-2 focus:ring-secondary/20"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-gray-700"
              >
                Phone Number
              </label>

              <PhoneInput
                country="us"
                value={phone}
                onChange={(value) =>
                  setPhone(validatePhoneNumber(value, inputRef.current))
                }
                enableSearch
                countryCodeEditable={false}
                inputProps={{
                  ref: inputRef,
                  required: true,
                }}
                containerClass="!w-full"
                dropdownClass="!z-50"
                buttonClass="!border-r !border-gray-300 !rounded-l-lg !bg-transparent hover:!bg-gray-50"
                inputClass="!w-full !h-12 !rounded-lg !border !border-gray-300 !pl-14 !pr-4 !text-sm !text-gray-700 focus:!border-secondary focus:!ring-2 focus:!ring-secondary/20"
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="message"
                className="block text-sm font-medium text-gray-700"
              >
                Message
              </label>

              <textarea
                id="message"
                rows={6}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="How can we help you?"
                required
                className="w-full rounded-lg border border-gray-300 p-4 text-sm outline-none transition-colors
    focus:border-secondary focus:ring-2 focus:ring-secondary/20"
              />
            </div>

            <button
              type="submit"
              className="w-full rounded-xl bg-sky-600 py-4 font-semibold text-white hover:bg-sky-700 transition"
            >
              {isProcessing ? "Processing..." : "Send Message"}
            </button>
          </form>
        </div>
      </section>
    </>
  )
}

export default ContactUs;